// CS Harness motion layer for the Shopify Horizon theme: reveals content as it scrolls into
// view, animates the first headline word by word, and gives the first banner image a gentle
// parallax, all skipped when the visitor's device asks for reduced motion; and draws the logo's
// hollow "CS" with electricity flowing through it (end of file; still for reduced motion). Loaded from
// layout/theme.liquid with defer; cs-harness.css holds the matching styles.
(() => {
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function splitWords(heading) {
    if (heading.dataset.cshSplit || heading.children.length) return; // leave headings with links or markup alone
    heading.dataset.cshSplit = '1';
    const words = heading.textContent.trim().split(/\s+/);
    heading.textContent = '';
    words.forEach((word, i) => {
      const span = document.createElement('span');
      span.className = 'csh-word';
      span.style.setProperty('--i', i);
      span.textContent = word;
      heading.append(span, ' ');
    });
    heading.classList.add('csh-words');
  }

  let observer = null;
  function init() {
    const main = document.querySelector('main') || document.body;
    const sections = [...main.querySelectorAll(':scope > section, :scope > div > section, .shopify-section')];
    const first = sections[0] || null;
    const own = el => el.closest('.csh'); // the custom home section animates itself
    const headings = [...main.querySelectorAll('h1, h2')].filter(h => !own(h));
    headings.forEach((h, n) => {
      if (n === 0) splitWords(h);
      else h.classList.add('csh-reveal', 'csh-accent-line');
    });
    main.querySelectorAll('p, img, .product-card, .resource-card').forEach((el, n) => {
      if ((first && first.contains(el)) || own(el)) return; // the first banner and the home section animate on their own
      el.classList.add('csh-reveal');
      el.style.setProperty('--csh-delay', `${(n % 4) * 80}ms`);
    });
    observer?.disconnect();
    observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }), {threshold: 0.15});
    main.querySelectorAll('.csh-reveal:not(.is-visible)').forEach(el => observer.observe(el));

    const heroImage = first?.querySelector('img');
    if (heroImage && !heroImage.dataset.cshParallax) {
      heroImage.dataset.cshParallax = '1';
      heroImage.style.willChange = 'transform';
      const move = () => { heroImage.style.transform = `translateY(${Math.min(scrollY, 700) * 0.15}px) scale(1.06)`; };
      addEventListener('scroll', () => requestAnimationFrame(move), {passive: true});
      move();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init); // the theme editor re-renders sections
})();

// CS Harness logo: the "CS" of the header logo drawn hollow, its outline a glowing wire with
// electricity flowing inside it. Pulses of current travel along the outline of each letter
// (white-hot core, blue glow, a crackle that changes every frame), clipped to the width of the
// line so nothing leaves it, and a bigger surge runs the whole way round every few seconds. The current casts a soft blue light on "Harness" and behind the logo (--csh-light).
// Drawn on a canvas that pauses off screen and in background tabs. The logo text stays for screen readers and search
// engines. Visitors whose device asks for reduced motion get a still, lit outline.
(() => {
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const BLUE = '#1ea7ff', CYAN = '#00e0ff', HOT = '#f4fdff';
  const rand = (a, b) => a + Math.random() * (b - a);
  // 8-neighbourhood, clockwise on screen, for tracing outlines.
  const DIRS = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
  const dirOf = (dx, dy) => DIRS.findIndex(([x, y]) => x === dx && y === dy);

  // Ordered outline loops of the filled pixels (Moore-neighbour tracing).
  function traceOutlines(inside, w, h) {
    const seen = new Set(), loops = [];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!inside(x, y) || inside(x - 1, y) || seen.has(y * w + x)) continue;
      const start = {x, y}, loop = [start];
      let p = start, back = {x: x - 1, y};
      seen.add(y * w + x);
      for (let guard = 0; guard < w * h; guard++) {
        const from = dirOf(back.x - p.x, back.y - p.y);
        let next = null;
        for (let k = 1; k <= 8 && !next; k++) {
          const d = (from + k) % 8, n = {x: p.x + DIRS[d][0], y: p.y + DIRS[d][1]};
          if (inside(n.x, n.y)) { next = n; const b = DIRS[(d + 7) % 8]; back = {x: p.x + b[0], y: p.y + b[1]}; }
        }
        if (!next || (next.x === start.x && next.y === start.y)) break;
        p = next; seen.add(p.y * w + p.x); loop.push(p);
      }
      if (loop.length > 24) loops.push(loop);
    }
    return loops;
  }

  class Logo {
    constructor(label) {
      const text = label.textContent.trim();
      label.dataset.cshLogo = '1';
      label.textContent = '';
      const spoken = document.createElement('span');
      spoken.className = 'visually-hidden';
      spoken.textContent = text;
      const visual = document.createElement('span');
      visual.className = 'csh-logo';
      visual.setAttribute('aria-hidden', 'true');
      this.box = document.createElement('span');
      this.box.className = 'csh-logo-cs';
      this.canvas = document.createElement('canvas');
      this.box.append(this.canvas);
      const rest = document.createElement('span');
      rest.className = 'csh-logo-rest';
      rest.textContent = text.slice(2).trim();
      visual.append(this.box, rest);
      label.append(spoken, visual);
      this.last = 0; this.visible = true;
      this.fit();
      if (still) { this.render(0); return; }
      new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.wake(); }).observe(this.box);
      this.wake();
    }

    // Measures the letters in the logo's own font and traces their outlines.
    fit() {
      const style = getComputedStyle(this.box.parentElement);
      this.size = parseFloat(style.fontSize) * 1.6;
      this.font = `800 ${this.size}px ${style.fontFamily}`;
      const probe = document.createElement('canvas').getContext('2d', {willReadFrequently: true});
      probe.font = this.font;
      const m = probe.measureText('CS');
      const w = Math.ceil(m.width), ascent = Math.ceil(m.actualBoundingBoxAscent), h = ascent + Math.ceil(m.actualBoundingBoxDescent);
      this.w = w; this.h = h; this.ascent = ascent;
      this.margin = Math.round(this.size * 0.5); // room for the glow
      this.box.style.width = `${w}px`;
      this.box.style.height = `${h}px`;
      const cw = w + 2 * this.margin, ch = h + 2 * this.margin, dpr = Math.min(devicePixelRatio || 1, 2);
      Object.assign(this.canvas.style, {width: `${cw}px`, height: `${ch}px`, left: `${-this.margin}px`, top: `${-this.margin}px`,
        maxWidth: 'none', maxHeight: 'none'}); // themes often cap canvases at 100% wide, which squashes the letters
      this.canvas.width = Math.round(cw * dpr); this.canvas.height = Math.round(ch * dpr);
      this.ctx = this.canvas.getContext('2d');
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // The current is drawn on its own layer and cut to the letter shapes before it is shown.
      this.layer = document.createElement('canvas').getContext('2d');
      this.layer.canvas.width = this.canvas.width; this.layer.canvas.height = this.canvas.height;
      this.layer.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Draw at 2x for smoother outlines, then trace.
      const s = 2, pw = (w + 2) * s, ph = (h + 2) * s;
      probe.canvas.width = pw; probe.canvas.height = ph;
      probe.font = `800 ${this.size * s}px ${style.fontFamily}`;
      probe.fillText('CS', s, (ascent + 1) * s);
      const alpha = probe.getImageData(0, 0, pw, ph).data;
      const inside = (x, y) => x >= 0 && y >= 0 && x < pw && y < ph && alpha[(y * pw + x) * 4 + 3] >= 128;
      this.loops = traceOutlines(inside, pw, ph).map(loop => {
        const pts = loop.filter((_, i) => i % 2 === 0).map(p => ({x: p.x / s - 1 + this.margin, y: p.y / s - 1 + this.margin}));
        const n = pts.length;
        return pts.map((p, i) => { // inward unit normal from the neighbours
          const a = pts[(i - 2 + n) % n], b = pts[(i + 2) % n], tx = b.x - a.x, ty = b.y - a.y, len = Math.hypot(tx, ty) || 1;
          let nx = ty / len, ny = -tx / len;
          const px = Math.round((p.x - this.margin + 1 + nx * 2) * s), py = Math.round((p.y - this.margin + 1 + ny * 2) * s);
          if (!inside(px, py)) { nx = -nx; ny = -ny; }
          return {x: p.x, y: p.y, nx, ny};
        });
      });
      // Pulses of current: three per outline, spread round it, each its own speed and length.
      this.pulses = this.loops.flatMap((loop, li) => [0, 1].map(k => ({loop: li, pos: (k / 2 + rand(0, 0.15)) * loop.length,
        speed: rand(0.9, 1.4), len: Math.round(loop.length * rand(0.1, 0.18)), surge: false})));
      this.nextSurge = performance.now() + rand(600, 1800);
    }

    wake() { if (!this.running && this.visible && !document.hidden && !still) { this.running = true; requestAnimationFrame(t => this.frame(t)); } }


    frame(now) {
      if (!this.visible || document.hidden) { this.running = false; return; }
      const dt = Math.min(0.05, (now - (this.last || now)) / 1000);
      this.last = now;
      if (now >= this.nextSurge && this.loops.length) { // a surge: a long, bright pulse right round one letter
        const li = (Math.random() * this.loops.length) | 0, n = this.loops[li].length;
        this.pulses.push({loop: li, pos: rand(0, n), speed: 2.6, len: Math.round(n * 0.35), surge: true, travelled: 0});
        this.nextSurge = now + rand(1500, 3500);
        if (Math.random() < 0.25) this.flashUntil = now + rand(60, 100);
      }
      const surging = this.pulses.some(p => p.surge);
      this.light = (this.light ?? 0.5) * 0.85 + (surging ? 1 : rand(0.35, 0.65)) * 0.15;
      if ((this.tick = (this.tick || 0) + 1) % 3 === 0) this.box.parentElement.style.setProperty('--csh-light', this.light.toFixed(2));
      for (const p of this.pulses) {
        const loop = this.loops[p.loop], n = loop.length, step = p.speed * n * dt * 0.55; // about two seconds a lap
        p.pos = (p.pos + step) % n;
        if (p.surge) p.travelled += step;
      }
      this.pulses = this.pulses.filter(p => !p.surge || p.travelled < this.loops[p.loop].length);
      this.render(now);
      requestAnimationFrame(t => this.frame(t));
    }

    // One pulse: the outline from its tail to its head, knocked off the line by fresh random
    // crackle every frame, brightest at the head.
    drawPulse(c, p) {
      const loop = this.loops[p.loop], n = loop.length, jag = Math.max(1.1, this.size * (p.surge ? 0.035 : 0.028));
      const pts = [], every = Math.max(2, Math.round(p.len / 14));
      for (let i = 0; i <= p.len; i += every) {
        const q = loop[(p.pos + i) % n | 0], o = i === 0 || i + every > p.len ? 0 : rand(-jag, jag);
        pts.push({x: q.x + q.nx * o, y: q.y + q.ny * o});
      }
      for (const [width, color, blur, alpha] of [[this.size * 0.07, BLUE, this.size * 0.04, 0.55], [this.size * 0.028, CYAN, this.size * 0.02, 0.8]]) {
        c.strokeStyle = color; c.shadowColor = color === HOT ? CYAN : BLUE; c.shadowBlur = blur; c.lineWidth = Math.max(0.6, width);
        for (let i = 1; i < pts.length; i++) { // fade in from the tail to the head
          c.globalAlpha = alpha * (p.surge ? 1 : 0.85) * (i / pts.length);
          c.beginPath(); c.moveTo(pts[i - 1].x, pts[i - 1].y); c.lineTo(pts[i].x, pts[i].y); c.stroke();
        }
      }
    }

    render() {
      const c = this.ctx;
      c.clearRect(0, 0, this.w + 2 * this.margin, this.h + 2 * this.margin);
      c.lineJoin = 'round'; c.lineCap = 'round';
      // The hollow letters: a thin outline with a soft blue glow, flickering with the current.
      c.font = this.font;
      c.save();
      c.shadowColor = 'rgba(0, 0, 0, .85)'; c.shadowBlur = this.size * 0.12; c.shadowOffsetY = this.size * 0.06;
      c.strokeStyle = 'rgba(4, 10, 18, .9)'; c.lineWidth = Math.max(1.5, this.size * 0.045);
      c.strokeText('CS', this.margin, this.margin + this.ascent + this.size * 0.02);
      c.restore();
      // The wire: the outline itself, dim between pulses, with a soft glow round it.
      const x = this.margin, y = this.margin + this.ascent, wire = Math.max(1.1, this.size * 0.035);
      c.shadowColor = BLUE; c.shadowBlur = this.size * 0.07; // a tight glow, so the letters stay empty inside
      c.strokeStyle = 'rgba(30, 167, 255, .4)'; c.globalAlpha = still ? 1 : rand(0.85, 1);
      c.lineWidth = wire; c.strokeText('CS', x, y);
      c.globalAlpha = 1; c.shadowBlur = 0;
      const l = this.layer, cw = this.w + 2 * this.margin, ch = this.h + 2 * this.margin;
      l.globalCompositeOperation = 'source-over'; l.clearRect(0, 0, cw, ch);
      l.lineJoin = 'round'; l.lineCap = 'round';
      if (still) this.loops.forEach((loop, li) => this.drawPulse(l, {loop: li, pos: 0, len: Math.round(loop.length * 0.3), surge: false}));
      else for (const p of this.pulses) this.drawPulse(l, p);
      if (!still && performance.now() < (this.flashUntil || 0)) this.loops.forEach((loop, li) => this.drawPulse(l, {loop: li, pos: 0, len: loop.length - 1, surge: false}));
      l.globalAlpha = 1; l.shadowBlur = 0;
      l.globalCompositeOperation = 'destination-in'; // keep only what is inside the wire
      l.font = this.font; l.lineWidth = Math.max(4, wire * 3.5); l.strokeStyle = '#000'; l.strokeText('CS', x, y);
      // Where the current is, the wire lights up: a soft glow of the current itself, then the current.
      c.save();
      c.globalCompositeOperation = 'lighter';
      c.filter = `blur(${Math.max(1.5, this.size * 0.08)}px)`; c.globalAlpha = 0.45; c.drawImage(l.canvas, 0, 0, cw, ch); c.globalAlpha = 0.85;
      c.filter = 'none'; c.drawImage(l.canvas, 0, 0, cw, ch);
      c.restore();
    }
  }

  const logos = [];
  function init() {
    document.querySelectorAll('.header-logo span:not([class]), .header__heading-logo-text, [data-csh-logo-text]').forEach(label => {
      if (label.dataset.cshLogo || label.children.length || !/^CS\b/.test(label.textContent.trim())) return;
      logos.push(new Logo(label));
    });
  }
  const start = () => (document.fonts?.ready || Promise.resolve()).then(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('visibilitychange', () => logos.forEach(l => l.wake()));
  let timer = null;
  addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(() => logos.forEach(l => { l.fit(); if (still) l.render(); }), 150); });
  document.addEventListener('shopify:section:load', start); // the theme editor re-renders the header
})();
